import React, { FC, useState } from 'react'
import { Button, Well, Drawer, StatusBadge } from '@pushly/aqe/lib/components'
import { EditOutlined } from '@ant-design/icons'
import { Form, Select, Switch, Skeleton, Typography } from 'antd'
import { useService } from '@pushly/aqe/lib/hooks'
import { useForm } from 'antd/es/form/Form'
import titleCase from 'title-case'
import * as randomstring from 'randomstring'
import { AppService, DomainService } from '../../services'
import { DomainDto } from '../../dtos/domain'
import { DEFAULT_AI_GENERATION_POLICY, IAiGenerationPolicy } from '../../dtos/ai-generation'
import { getClassNames } from '../../_utils/classnames'
import { simpleFormErrorNotification } from '../../_utils/utils'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '../../constants'
import { SETTINGS_WELL_FORM_LAYOUT } from '../../theme/constants'

const EDIT_MODE_OPTIONS = [
    { value: 'locked', label: 'Locked' },
    { value: 'append_only', label: 'Append only' },
    { value: 'full', label: 'Full' },
]

const FALLBACK_MODE_OPTIONS = [
    { value: 'none', label: 'None' },
    { value: 'instruction_only', label: 'Instruction only' },
    { value: 'to_baseline', label: 'To baseline' },
]

const resolvePolicy = (domain: DomainDto): IAiGenerationPolicy =>
    domain.aiGenerationPolicy ?? DEFAULT_AI_GENERATION_POLICY

interface IAiPolicyEditor {
    domain: DomainDto
    visible: boolean
    onClose: (e: any) => void
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

const AiPolicyEditor: FC<IAiPolicyEditor> = (props) => {
    const appSvc = useService(AppService)
    const domainSvc = useService(DomainService)

    const [form] = useForm()
    const [isDirty, setIsDirty] = useState(false)

    const policy = resolvePolicy(props.domain)

    const handleSubmit = async () => {
        try {
            const values = (await form.validateFields()) as IAiGenerationPolicy

            const { ok, data } = await domainSvc.patchDomainById(props.domain.id, {
                aiGenerationPolicy: values,
            } as any)

            if (ok) {
                props.onDomainUpdated(data.legacyDomainDto)
                props.onClose(null)
            }
        } catch (err) {
            simpleFormErrorNotification(err)
        }
    }

    return (
        <Drawer
            getContainer={appSvc.getAppContainer}
            className={getClassNames('ddf-ai-policy-editor')}
            title="Update AI Generation Policy"
            placement="right"
            closable={true}
            onSubmit={handleSubmit}
            disableSubmit={!isDirty}
            onClose={props.onClose}
            open={props.visible}
            width={800}
        >
            <Form
                form={form}
                initialValues={policy}
                onValuesChange={() => setIsDirty(true)}
                {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                requiredMark={false}
                size="small"
                labelAlign="left"
            >
                <Form.Item
                    name="directiveEditMode"
                    label="Directive edit mode"
                    extra="How much an author may change a directive when applying it."
                >
                    <Select size="small" options={EDIT_MODE_OPTIONS} />
                </Form.Item>

                <Form.Item
                    name="fallbackMode"
                    label="Fallback mode"
                    extra="What generation does when governed rules can't be satisfied."
                >
                    <Select size="small" options={FALLBACK_MODE_OPTIONS} />
                </Form.Item>

                <Form.Item name="brandVoicePreview" label="Brand Voice preview" valuePropName="checked">
                    <Switch aria-label="Show a preview of the resolved Brand Voice before generation" />
                </Form.Item>

                <Form.Item name="allowInlineVoiceEdit" label="Allow inline voice edits" valuePropName="checked">
                    <Switch aria-label="Allow authors to override/edit the Brand Voice inline at generation time" />
                </Form.Item>

                <Form.Item name="requireDirective" label="Require directive" valuePropName="checked">
                    <Switch aria-label="Require a directive to be selected before AI generation" />
                </Form.Item>
            </Form>
        </Drawer>
    )
}

interface IAiPolicyWell {
    domain: DomainDto
    canEdit: boolean
    loading?: boolean
    onDomainUpdated: (domain: Partial<DomainDto>) => void
}

export const AiPolicyWell: FC<IAiPolicyWell> = (props) => {
    const [showDrawer, setShowDrawer] = useState(false)
    const [drawerKey, setDrawerKey] = useState<string>('')

    const policy = props.domain ? resolvePolicy(props.domain) : DEFAULT_AI_GENERATION_POLICY

    return (
        <Well
            className="nested"
            title="AI Generation Policy"
            actions={
                <Button
                    size="small"
                    onClick={() => {
                        setDrawerKey(randomstring.generate())
                        setShowDrawer(true)
                    }}
                    disabled={!props.canEdit || props.loading}
                >
                    <EditOutlined />
                    <span>Edit</span>
                </Button>
            }
            hideFooter={true}
        >
            <Skeleton loading={props.loading} active={true} title={false}>
                {!props.loading && (
                    <Form
                        {...SETTINGS_WELL_FORM_LAYOUT}
                        colon={false}
                        requiredMark={false}
                        labelAlign="left"
                        size="small"
                    >
                        <Form.Item label="Directive edit mode">
                            <Typography.Text>{titleCase(policy.directiveEditMode.replace(/_/g, ' '))}</Typography.Text>
                        </Form.Item>
                        <Form.Item label="Fallback mode">
                            <Typography.Text>{titleCase(policy.fallbackMode.replace(/_/g, ' '))}</Typography.Text>
                        </Form.Item>
                        <Form.Item label="Brand Voice preview">
                            <StatusBadge
                                type="generic"
                                expanded={true}
                                status={policy.brandVoicePreview ? 'ENABLED' : 'DISABLED'}
                            />
                        </Form.Item>
                        <Form.Item label="Allow inline voice edits">
                            <StatusBadge
                                type="generic"
                                expanded={true}
                                status={policy.allowInlineVoiceEdit ? 'ENABLED' : 'DISABLED'}
                            />
                        </Form.Item>
                        <Form.Item label="Require directive">
                            <StatusBadge
                                type="generic"
                                expanded={true}
                                status={policy.requireDirective ? 'ENABLED' : 'DISABLED'}
                            />
                        </Form.Item>
                    </Form>
                )}
            </Skeleton>

            {props.domain && (
                <AiPolicyEditor
                    key={drawerKey}
                    domain={props.domain}
                    visible={showDrawer}
                    onClose={() => setShowDrawer(false)}
                    onDomainUpdated={props.onDomainUpdated}
                />
            )}
        </Well>
    )
}
